// Blog1List.js - Contains functions for creating a blog listing section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Blog List section with multiple blog posts
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the blog section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The blog section elements
 */
export const createBlogElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const blogSectionClass = 'blog1-section';
  const blogContainerClass = 'blog1-container';
  const blogHeaderClass = 'blog1-header';
  const blogTitleClass = 'blog1-title';
  const blogSubtitleClass = 'blog1-subtitle';
  const blogPostsContainerClass = 'blog1-posts-container';
  const blogPostClass = 'blog1-post';
  const blogPostImageClass = 'blog1-post-image';
  const blogPostContentClass = 'blog1-post-content';
  const blogPostCategoryClass = 'blog1-post-category';
  const blogPostTitleClass = 'blog1-post-title';
  const blogPostExcerptClass = 'blog1-post-excerpt';
  const blogPostMetaClass = 'blog1-post-meta';
  const blogPostAuthorClass = 'blog1-post-author';
  const blogPostDateClass = 'blog1-post-date';
  const blogViewAllButtonClass = 'blog1-view-all-button';
  
  // Desktop Styles (default)
  const blogSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const blogContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const blogHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const blogTitleStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const blogSubtitleStyles = {
    fontSize: '18px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '30px'
  };

  const blogPostsContainerStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '30px',
    width: '100%'
  };

  const blogPostStyles = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    flex: '1',
    minWidth: '300px',
    maxWidth: '380px',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
  };

  const blogPostImageStyles = {
    width: '100%',
    height: '200px',
    objectFit: 'cover'
  };

  const blogPostContentStyles = {
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    flex: '1'
  };

  const blogPostCategoryStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#4361ee',
    textTransform: 'uppercase',
    marginBottom: '5px'
  };

  const blogPostTitleStyles = {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '10px',
    lineHeight: '1.3'
  };

  const blogPostExcerptStyles = {
    fontSize: '16px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '15px'
  };

  const blogPostMetaStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: '#888888',
    marginTop: 'auto'
  };

  const blogPostAuthorStyles = {
    fontWeight: '500'
  };

  const blogPostDateStyles = {
    fontStyle: 'italic'
  };

  const blogViewAllButtonStyles = {
    marginTop: '30px',
    padding: '12px 24px',
    backgroundColor: '#4361ee',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '500',
    borderRadius: '5px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  };

  // Tablet Styles
  const tabletBlogPostsContainerStyles = {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '20px'
  };

  const tabletBlogPostStyles = {
    minWidth: '280px',
    maxWidth: '320px'
  };

  // Mobile Styles
  const mobileBlogPostsContainerStyles = {
    flexDirection: 'column',
    alignItems: 'center'
  };

  const mobileBlogPostStyles = {
    width: '100%',
    maxWidth: '350px',
    margin: '0 auto'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(blogSectionClass, blogSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(blogContainerClass, blogContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(blogHeaderClass, blogHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(blogTitleClass, blogTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(blogSubtitleClass, blogSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostsContainerClass, blogPostsContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostClass, blogPostStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostImageClass, blogPostImageStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostContentClass, blogPostContentStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostCategoryClass, blogPostCategoryStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostTitleClass, blogPostTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostExcerptClass, blogPostExcerptStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostMetaClass, blogPostMetaStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostAuthorClass, blogPostAuthorStyles);
    newRegistry[DEVICES.DESKTOP].set(blogPostDateClass, blogPostDateStyles);
    newRegistry[DEVICES.DESKTOP].set(blogViewAllButtonClass, blogViewAllButtonStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(blogPostsContainerClass, tabletBlogPostsContainerStyles);
    newRegistry[DEVICES.TABLET].set(blogPostClass, tabletBlogPostStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(blogPostsContainerClass, mobileBlogPostsContainerStyles);
    newRegistry[DEVICES.MOBILE].set(blogPostClass, mobileBlogPostStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    blogSectionClass,
    blogContainerClass,
    blogHeaderClass,
    blogTitleClass,
    blogSubtitleClass,
    blogPostsContainerClass,
    blogPostClass,
    blogPostImageClass,
    blogPostContentClass,
    blogPostCategoryClass,
    blogPostTitleClass,
    blogPostExcerptClass,
    blogPostMetaClass,
    blogPostAuthorClass,
    blogPostDateClass,
    blogViewAllButtonClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  
  // Blog posts container
  const postsContainerId = Date.now() + 5;
  
  // Blog post 1
  const post1Id = Date.now() + 6;
  const post1ImageId = Date.now() + 7;
  const post1ContentId = Date.now() + 8;
  const post1CategoryId = Date.now() + 9;
  const post1TitleId = Date.now() + 10;
  const post1ExcerptId = Date.now() + 11;
  const post1MetaId = Date.now() + 12;
  const post1AuthorId = Date.now() + 13;
  const post1DateId = Date.now() + 14;
  
  // Blog post 2
  const post2Id = Date.now() + 15;
  const post2ImageId = Date.now() + 16;
  const post2ContentId = Date.now() + 17;
  const post2CategoryId = Date.now() + 18;
  const post2TitleId = Date.now() + 19;
  const post2ExcerptId = Date.now() + 20;
  const post2MetaId = Date.now() + 21;
  const post2AuthorId = Date.now() + 22;
  const post2DateId = Date.now() + 23;
  
  // Blog post 3
  const post3Id = Date.now() + 24;
  const post3ImageId = Date.now() + 25;
  const post3ContentId = Date.now() + 26;
  const post3CategoryId = Date.now() + 27;
  const post3TitleId = Date.now() + 28;
  const post3ExcerptId = Date.now() + 29;
  const post3MetaId = Date.now() + 30;
  const post3AuthorId = Date.now() + 31;
  const post3DateId = Date.now() + 32;
  
  // View all button
  const viewAllButtonId = Date.now() + 33;

  // Create elements structure
  const newElements = [
    // Main Blog Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Blog Section',
      parentId: null,
      className: blogSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'blog-section',
        'data-component': 'section',
        'data-section-type': 'blog',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: blogContainerClass,
      styles: {},
      children: [headerId, postsContainerId, viewAllButtonId],
      text: '',
      attributes: {
        'data-element': 'blog-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: blogHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'blog-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Blog Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: blogTitleClass,
      styles: {},
      children: [],
      text: 'Latest From Our Blog',
      attributes: {
        'data-element': 'blog-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Blog Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: blogSubtitleClass,
      styles: {},
      children: [],
      text: 'Stay up to date with our latest articles and news',
      attributes: {
        'data-element': 'blog-subtitle'
      }
    },
    
    // Blog Posts Container
    {
      id: postsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Posts Container',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostsContainerClass,
      styles: {},
      children: [post1Id, post2Id, post3Id],
      text: '',
      attributes: {
        'data-element': 'blog-posts-container'
      }
    },
    
    // Blog Post 1
    {
      id: post1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 1',
      parentId: postsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostClass,
      styles: {},
      children: [post1ImageId, post1ContentId],
      text: '',
      attributes: {
        'data-element': 'blog-post'
      }
    },
    
    // Blog Post 1 Image
    {
      id: post1ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Blog Post 1 Image',
      parentId: post1Id,
      hierarchyName: getHierarchyName('image'),
      className: blogPostImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1581287053822-fd7bf4f4bfec?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'blog-post-image',
        'alt': 'Web Design Trends'
      }
    },
    
    // Blog Post 1 Content
    {
      id: post1ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 1 Content',
      parentId: post1Id,
      hierarchyName: getHierarchyName('div'),
      className: blogPostContentClass,
      styles: {},
      children: [post1CategoryId, post1TitleId, post1ExcerptId, post1MetaId],
      text: '',
      attributes: {
        'data-element': 'blog-post-content'
      }
    },
    
    // Blog Post 1 Category
    {
      id: post1CategoryId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 1 Category',
      parentId: post1ContentId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostCategoryClass,
      styles: {},
      children: [],
      text: 'Design',
      attributes: {
        'data-element': 'blog-post-category'
      }
    },
    
    // Blog Post 1 Title
    {
      id: post1TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Blog Post 1 Title',
      parentId: post1ContentId,
      hierarchyName: getHierarchyName('h3'),
      className: blogPostTitleClass,
      styles: {},
      children: [],
      text: '10 Web Design Trends to Watch in 2023',
      attributes: {
        'data-element': 'blog-post-title'
      }
    },
    
    // Blog Post 1 Excerpt
    {
      id: post1ExcerptId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Blog Post 1 Excerpt',
      parentId: post1ContentId,
      hierarchyName: getHierarchyName('p'),
      className: blogPostExcerptClass,
      styles: {},
      children: [],
      text: 'Discover the latest web design trends that are dominating the digital landscape this year.',
      attributes: {
        'data-element': 'blog-post-excerpt'
      }
    },
    
    // Blog Post 1 Meta
    {
      id: post1MetaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 1 Meta',
      parentId: post1ContentId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostMetaClass,
      styles: {},
      children: [post1AuthorId, post1DateId],
      text: '',
      attributes: {
        'data-element': 'blog-post-meta'
      }
    },
    
    // Blog Post 1 Author
    {
      id: post1AuthorId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 1 Author',
      parentId: post1MetaId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostAuthorClass,
      styles: {},
      children: [],
      text: 'By Sarah Johnson',
      attributes: {
        'data-element': 'blog-post-author'
      }
    },
    
    // Blog Post 1 Date
    {
      id: post1DateId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 1 Date',
      parentId: post1MetaId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostDateClass,
      styles: {},
      children: [],
      text: 'March 15, 2023',
      attributes: {
        'data-element': 'blog-post-date'
      }
    },
    
    // Blog Post 2
    {
      id: post2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 2',
      parentId: postsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostClass,
      styles: {},
      children: [post2ImageId, post2ContentId],
      text: '',
      attributes: {
        'data-element': 'blog-post'
      }
    },
    
    // Blog Post 2 Image
    {
      id: post2ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Blog Post 2 Image',
      parentId: post2Id,
      hierarchyName: getHierarchyName('image'),
      className: blogPostImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'blog-post-image',
        'alt': 'Digital Marketing Strategy'
      }
    },
    
    // Blog Post 2 Content
    {
      id: post2ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 2 Content',
      parentId: post2Id,
      hierarchyName: getHierarchyName('div'),
      className: blogPostContentClass,
      styles: {},
      children: [post2CategoryId, post2TitleId, post2ExcerptId, post2MetaId],
      text: '',
      attributes: {
        'data-element': 'blog-post-content'
      }
    },
    
    // Blog Post 2 Category
    {
      id: post2CategoryId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 2 Category',
      parentId: post2ContentId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostCategoryClass,
      styles: {},
      children: [],
      text: 'Marketing',
      attributes: {
        'data-element': 'blog-post-category'
      }
    },
    
    // Blog Post 2 Title
    {
      id: post2TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Blog Post 2 Title',
      parentId: post2ContentId,
      hierarchyName: getHierarchyName('h3'),
      className: blogPostTitleClass,
      styles: {},
      children: [],
      text: 'The Ultimate Guide to Digital Marketing in 2023',
      attributes: {
        'data-element': 'blog-post-title'
      }
    },
    
    // Blog Post 2 Excerpt
    {
      id: post2ExcerptId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Blog Post 2 Excerpt',
      parentId: post2ContentId,
      hierarchyName: getHierarchyName('p'),
      className: blogPostExcerptClass,
      styles: {},
      children: [],
      text: 'Learn effective strategies to grow your business online and reach your target audience.',
      attributes: {
        'data-element': 'blog-post-excerpt'
      }
    },
    
    // Blog Post 2 Meta
    {
      id: post2MetaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 2 Meta',
      parentId: post2ContentId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostMetaClass,
      styles: {},
      children: [post2AuthorId, post2DateId],
      text: '',
      attributes: {
        'data-element': 'blog-post-meta'
      }
    },
    
    // Blog Post 2 Author
    {
      id: post2AuthorId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 2 Author',
      parentId: post2MetaId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostAuthorClass,
      styles: {},
      children: [],
      text: 'By Michael Brown',
      attributes: {
        'data-element': 'blog-post-author'
      }
    },
    
    // Blog Post 2 Date
    {
      id: post2DateId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 2 Date',
      parentId: post2MetaId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostDateClass,
      styles: {},
      children: [],
      text: 'April 2, 2023',
      attributes: {
        'data-element': 'blog-post-date'
      }
    },
    
    // Blog Post 3
    {
      id: post3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 3',
      parentId: postsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostClass,
      styles: {},
      children: [post3ImageId, post3ContentId],
      text: '',
      attributes: {
        'data-element': 'blog-post'
      }
    },
    
    // Blog Post 3 Image
    {
      id: post3ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Blog Post 3 Image',
      parentId: post3Id,
      hierarchyName: getHierarchyName('image'),
      className: blogPostImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'blog-post-image',
        'alt': 'Development Tips'
      }
    },
    
    // Blog Post 3 Content
    {
      id: post3ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 3 Content',
      parentId: post3Id,
      hierarchyName: getHierarchyName('div'),
      className: blogPostContentClass,
      styles: {},
      children: [post3CategoryId, post3TitleId, post3ExcerptId, post3MetaId],
      text: '',
      attributes: {
        'data-element': 'blog-post-content'
      }
    },
    
    // Blog Post 3 Category
    {
      id: post3CategoryId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 3 Category',
      parentId: post3ContentId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostCategoryClass,
      styles: {},
      children: [],
      text: 'Development',
      attributes: {
        'data-element': 'blog-post-category'
      }
    },
    
    // Blog Post 3 Title
    {
      id: post3TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Blog Post 3 Title',
      parentId: post3ContentId,
      hierarchyName: getHierarchyName('h3'),
      className: blogPostTitleClass,
      styles: {},
      children: [],
      text: '5 Developer Productivity Tips to Boost Your Workflow',
      attributes: {
        'data-element': 'blog-post-title'
      }
    },
    
    // Blog Post 3 Excerpt
    {
      id: post3ExcerptId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Blog Post 3 Excerpt',
      parentId: post3ContentId,
      hierarchyName: getHierarchyName('p'),
      className: blogPostExcerptClass,
      styles: {},
      children: [],
      text: 'Improve your productivity with these proven tips and tools for modern web developers.',
      attributes: {
        'data-element': 'blog-post-excerpt'
      }
    },
    
    // Blog Post 3 Meta
    {
      id: post3MetaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 3 Meta',
      parentId: post3ContentId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostMetaClass,
      styles: {},
      children: [post3AuthorId, post3DateId],
      text: '',
      attributes: {
        'data-element': 'blog-post-meta'
      }
    },
    
    // Blog Post 3 Author
    {
      id: post3AuthorId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 3 Author',
      parentId: post3MetaId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostAuthorClass,
      styles: {},
      children: [],
      text: 'By David Lee',
      attributes: {
        'data-element': 'blog-post-author'
      }
    },
    
    // Blog Post 3 Date
    {
      id: post3DateId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Blog Post 3 Date',
      parentId: post3MetaId,
      hierarchyName: getHierarchyName('div'),
      className: blogPostDateClass,
      styles: {},
      children: [],
      text: 'April 18, 2023',
      attributes: {
        'data-element': 'blog-post-date'
      }
    },
    
    // View All Button
    {
      id: viewAllButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'View All Button',
      parentId: containerId,
      hierarchyName: getHierarchyName('button'),
      className: blogViewAllButtonClass,
      styles: {},
      children: [],
      text: 'View All Posts',
      attributes: {
        'data-element': 'blog-view-all-button'
      }
    }
  ];
  
  return {
    blogSection: newElements[0],
    elements: newElements
  };
};

export default createBlogElements; 